import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, MessagesSquare } from 'lucide-react'
import {
  useResource, statusTone, cn,
  PageHeader, Card, Badge, Button, EmptyState, LoadingState, ErrorState,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@retink/web-ui'
import { api } from '../lib/api'

// The server's default (and the viewer's) page size — a full page means there
// may be more, so the load-more control stays until a short page comes back.
const PAGE = 50

// The Conversation Viewer (conversations.md open move #2, kev-ruled 2026-07-08):
// a READ-ONLY conversation/interaction browser for ANY tenant user — part of the
// all-user observability pages (trace spine). No actions: the desk keeps the
// operator working view; this is a viewer (no claim/close/reply). Reads go
// SPA → tenant /api/observe → S2S → CC record reads.

interface ConversationRow {
  conversationId: string; channel: string; direction: string; status: string
  ownerKind: string; ownerRef: string | null; handoffState: string | null
  externalRef: string | null; latestInteractionId: string | null
  openedAt: string; closedAt: string | null; updatedAt: string
}
interface ConversationEvent {
  seq: number; type: string; actorKind: string; actorRef: string | null
  payload: Record<string, unknown>; occurredAt: string
}
interface InteractionEvent { id: string; type: string; payload: Record<string, unknown>; at: string }
interface Interaction {
  interactionId: string; status: string; openedAt: string; closedAt: string | null
  closedByKind: string | null; closedReason: string | null; events: InteractionEvent[]
}
interface Trace {
  conversation: ConversationRow
  events: ConversationEvent[]
  interactions: Interaction[]
}

const STATUSES = ['', 'active', 'parked', 'closed'] as const
const CHANNELS = ['', 'voice', 'chat', 'sms', 'email', 'api'] as const

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

// ─── List ────────────────────────────────────────────────────────────────────
export function Conversations() {
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? ''
  const channel = params.get('channel') ?? ''

  const { data, loading, error, reload } = useResource<{ conversations: ConversationRow[] }>(() => {
    const q = new URLSearchParams()
    if (status) q.set('status', status)
    if (channel) q.set('channel', channel)
    const qs = q.toString()
    return api.get(`/api/observe/conversations${qs ? `?${qs}` : ''}`)
  }, [status, channel])

  // Older pages fetched via load-more, keyset-cursored on the last row's
  // (openedAt, conversationId) — appended to the first page useResource holds.
  const [morePages, setMorePages] = useState<ConversationRow[][]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [done, setDone] = useState(false)
  // A filter change re-runs useResource for a fresh first page — drop the tail.
  // The generation counter kills the race where a load-more launched under the
  // OLD filters resolves after the change and appends into the new list.
  const filterGen = useRef(0)
  useEffect(() => { filterGen.current++; setMorePages([]); setDone(false) }, [status, channel])

  const setFilter = (key: 'status' | 'channel', value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const firstPage = data?.conversations ?? []
  const rows = [...firstPage, ...morePages.flat()]

  const loadMore = async () => {
    const last = rows[rows.length - 1]
    if (!last) return
    const gen = filterGen.current
    setLoadingMore(true)
    try {
      const q = new URLSearchParams()
      if (status) q.set('status', status)
      if (channel) q.set('channel', channel)
      q.set('before', last.openedAt)
      q.set('beforeId', last.conversationId)
      const next = await api.get<{ conversations: ConversationRow[] }>(`/api/observe/conversations?${q}`)
      if (gen !== filterGen.current) return // filters changed mid-flight — stale page
      setMorePages((p) => [...p, next.conversations])
      if (next.conversations.length < PAGE) setDone(true)
    } finally {
      setLoadingMore(false)
    }
  }
  // Show the control while the first page is full and no short page has ended it.
  const canLoadMore = !done && firstPage.length >= PAGE

  return (
    <div className="p-8">
      <PageHeader
        title="Conversations"
        description="A read-only view of every conversation in your tenant and the interactions behind it."
      />

      <div className="mb-4 space-y-2">
        <ChipRow label="Status" values={STATUSES} active={status} onPick={(v) => setFilter('status', v)} />
        <ChipRow label="Channel" values={CHANNELS} active={channel} onPick={(v) => setFilter('channel', v)} />
      </div>

      {loading ? (
        <LoadingState label="Loading conversations…" />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={MessagesSquare}
            title="No conversations"
            description="Conversations appear here as they flow through your channels and flows."
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Opened</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.conversationId} className="cursor-pointer">
                  <TableCell>
                    <Link to={`/conversations/${r.conversationId}`} className="flex items-center gap-2 font-medium hover:underline">
                      <MessagesSquare size={14} className="shrink-0 text-muted-foreground" />
                      {r.channel}
                      <span className="text-xs text-muted-foreground">{r.direction}</span>
                    </Link>
                  </TableCell>
                  <TableCell><Badge tone={statusTone(r.status)}>{r.status}</Badge></TableCell>
                  <TableCell>
                    <span className="text-sm">{r.ownerKind}</span>
                    {r.handoffState && <span className="ml-1 text-xs text-muted-foreground">· {r.handoffState}</span>}
                  </TableCell>
                  <TableCell mono>{r.externalRef ?? '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmt(r.openedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!loading && !error && canLoadMore && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}

function ChipRow({ label, values, active, onPick }: {
  label: string; values: readonly string[]; active: string; onPick: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {values.map((v) => {
        const on = v === active
        return (
          <button
            key={v || 'all'}
            type="button"
            onClick={() => onPick(v)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              on
                ? 'border-[var(--surface-tenant)] bg-accent text-accent-foreground'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {v || 'All'}
          </button>
        )
      })}
    </div>
  )
}

// ─── Detail ──────────────────────────────────────────────────────────────────
export function ConversationDetail() {
  const { id = '' } = useParams()
  const { data, loading, error, reload } = useResource<Trace>(
    () => api.get(`/api/observe/conversations/${id}`), [id],
  )

  return (
    <div className="p-8">
      <Link to="/conversations" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> Conversations
      </Link>

      {loading ? (
        <LoadingState label="Loading conversation…" />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !data ? null : (
        <>
          <PageHeader
            title={<span className="capitalize">{data.conversation.channel} conversation</span>}
            description={
              <span className="font-mono">
                {data.conversation.direction} · {data.conversation.externalRef ?? 'no contact'} · opened {fmt(data.conversation.openedAt)}
              </span>
            }
          />
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(data.conversation.status)}>{data.conversation.status}</Badge>
            <Badge tone="outline">{data.conversation.ownerKind}</Badge>
            {data.conversation.handoffState && <Badge tone="outline">{data.conversation.handoffState}</Badge>}
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
            <Timeline events={data.events} />
            <Interactions interactions={data.interactions} />
          </div>
        </>
      )}
    </div>
  )
}

// The message/event stream — a 'message' payload renders its text plainly; any
// other event type renders as a labelled row (type + actor + time).
function Timeline({ events }: { events: ConversationEvent[] }) {
  return (
    <section>
      <h2 className="mb-3 text-sm font-medium">Timeline</h2>
      {events.length === 0 ? (
        <Card><EmptyState title="No events yet" /></Card>
      ) : (
        <ol className="space-y-2">
          {events.map((e) => <EventRow key={e.seq} e={e} />)}
        </ol>
      )}
    </section>
  )
}

function messageText(payload: Record<string, unknown>): string | null {
  if (typeof payload['text'] === 'string') return payload['text']
  const part = payload['part']
  if (part && typeof part === 'object' && typeof (part as { text?: unknown }).text === 'string') {
    return (part as { text: string }).text
  }
  return null
}

function EventRow({ e }: { e: ConversationEvent }) {
  const isMessage = e.type === 'message'
  const text = isMessage ? messageText(e.payload) : null
  const direction = typeof e.payload['direction'] === 'string' ? (e.payload['direction'] as string) : null

  if (isMessage && text != null) {
    const inbound = direction === 'inbound'
    return (
      <li className={cn('flex', inbound ? 'justify-start' : 'justify-end')}>
        <div className={cn(
          'max-w-[80%] rounded-lg border px-3 py-2 text-sm',
          inbound ? 'border-border bg-card' : 'border-[var(--surface-tenant)]/30 bg-accent',
        )}>
          <div className="mb-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{e.actorKind}{e.actorRef ? ` · ${e.actorRef}` : ''}</span>
            <span>{fmt(e.occurredAt)}</span>
          </div>
          <div className="whitespace-pre-wrap break-words">{text}</div>
        </div>
      </li>
    )
  }

  return (
    <li className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-1.5 text-xs">
      <Badge tone="neutral">{e.type}</Badge>
      <span className="text-muted-foreground">{e.actorKind}{e.actorRef ? ` · ${e.actorRef}` : ''}</span>
      <span className="ml-auto text-muted-foreground">{fmt(e.occurredAt)}</span>
    </li>
  )
}

// The interaction legs of the conversation + each leg's carrier-lifecycle events
// (interaction_event) — the trace-spine payoff.
function Interactions({ interactions }: { interactions: Interaction[] }) {
  return (
    <section>
      <h2 className="mb-3 text-sm font-medium">Interactions</h2>
      {interactions.length === 0 ? (
        <Card><EmptyState title="No interactions" /></Card>
      ) : (
        <div className="space-y-3">
          {interactions.map((i) => (
            <Card key={i.interactionId} className="p-3">
              <div className="flex items-center justify-between gap-2">
                <Badge tone={i.status === 'closed' ? 'neutral' : 'success'}>{i.status}</Badge>
                <span className="font-mono text-[11px] text-muted-foreground">{i.interactionId.slice(0, 8)}</span>
              </div>
              <dl className="mt-2 grid grid-cols-[4.5rem_1fr] gap-y-0.5 text-xs">
                <dt className="text-muted-foreground">Opened</dt><dd>{fmt(i.openedAt)}</dd>
                <dt className="text-muted-foreground">Closed</dt><dd>{fmt(i.closedAt)}</dd>
                {i.closedReason && (<><dt className="text-muted-foreground">Reason</dt><dd>{i.closedReason}</dd></>)}
              </dl>
              {i.events.length > 0 && (
                <ol className="mt-2 space-y-1 border-t border-border pt-2">
                  {i.events.map((ev) => (
                    <li key={ev.id} className="flex items-center gap-2 text-[11px]">
                      <span className="font-medium">{ev.type}</span>
                      <span className="ml-auto text-muted-foreground">{fmt(ev.at)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          ))}
        </div>
      )}
    </section>
  )
}
